import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useStopwatch } from "react-timer-hook";
import { getNotifMsg } from "utils/message";
import "./styles.scss";
import { sendFileMessageStart } from "redux/chats/chats.actions";
import { verifyFileToOpen } from "utils/validation";
import AudioAnalyser from "./components/AudioAnalyser";
import {
  MicIconComponent,
  NewSendIcon,
  PauseIconComponent,
} from "assets/icons";
import { TrashFilledIcon } from "assets/icons";
import useTranslation from "hooks/useTranslation";
import {
  resetVoiceRecordingData,
  setAudioStream,
  setRecordingAudio,
  setVoiceRecording,
} from "redux/voice/voice.actions";

const getLocale = ({ settingsData }) => settingsData.locale;
const getAudioStream = ({ voiceMsgData }) => voiceMsgData.audioStream;
const getSelectedConversation = ({ chatsData }) =>
  chatsData.selectedConversation;
const getRecordingAudio = ({ voiceMsgData }) => voiceMsgData.recordingAudio;
const getVoiceRecording = ({ voiceMsgData }) => voiceMsgData.voiceRecording;

function VoiceRecorder() {
  const locale = useSelector(getLocale);
  const audioStream = useSelector(getAudioStream);
  const selectedConversation = useSelector(getSelectedConversation);
  const recordingAudio = useSelector(getRecordingAudio);
  const voiceRecording = useSelector(getVoiceRecording);

  const [mediaRecorder, setMediaRecorder] = useState();
  const [chunks, setChunks] = useState([]);
  const [pauseAudio, setPauseAudio] = useState(false);
  const [file, setFile] = useState(null);
  const dispatch = useDispatch();
  const { getTranslation } = useTranslation("CHAT");
  const { t } = getTranslation();

  const { seconds, minutes, start, reset, pause } = useStopwatch({
    autoStart: false,
  });

  const getStream = async () => {
    if (!audioStream) {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      dispatch(setAudioStream(stream));

      return stream;
    }

    return audioStream;
  };

  const saveFileWhenPausing = (file) => {
    const fileMessage = verifyFileToOpen(file);
    const errorMessage = getNotifMsg("INVALID_FILE_FORMAT", locale);

    if (fileMessage === null) toast.error(errorMessage);
    else setFile(fileMessage);
  };

  const saveAudio = (isPauseFile) => {
    const blob = new Blob(chunks, { type: "audio/mpeg" });
    const file = new File([blob], "voice", {
      lastModified: new Date().getTime(),
      type: blob.type,
    });

    if (isPauseFile) {
      saveFileWhenPausing(file);
      return;
    }

    dispatch(
      sendFileMessageStart({
        conversationId: selectedConversation.conversation.id,
        clientId: selectedConversation.user.id,
        file: file,
        size: file.size,
      })
    );
  };

  const clearAudioStream = () => {
    audioStream
      ?.getTracks() // get all tracks from the MediaStream
      ?.forEach((track) => track?.stop()); // stop each of them
    dispatch(resetVoiceRecordingData());
  };

  const handleSendButtonClick = (e) => {
    e?.preventDefault();
    dispatch(setRecordingAudio(null));

    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder?.stop();
      dispatch(setVoiceRecording(false));
      reset();
      saveAudio();
    }
  };

  const handleClickStartRecordBtn = async (e) => {
    e.preventDefault();

    const stream = await getStream();

    let t = new MediaRecorder(stream);
    setMediaRecorder(t);
    setChunks([]);

    dispatch(setRecordingAudio(stream));
    if (t.state !== "recording") {
      setChunks([]);
      t.start(10);
      dispatch(setVoiceRecording(true));
      start();
    }
  };

  const handleDiscardBtnClick = () => {
    if (
      (mediaRecorder && mediaRecorder.state !== "inactive") ||
      recordingAudio
    ) {
      mediaRecorder?.stop();
      dispatch(setVoiceRecording(false));
      reset();
    }
    dispatch(setRecordingAudio(null));
    setFile(null);
    setPauseAudio(false);
    clearAudioStream();
  };

  const handlePauseVoiceRecording = (e) => {
    e.preventDefault();
    if (mediaRecorder) {
      if (mediaRecorder.state === "recording") {
        mediaRecorder.pause();
        setPauseAudio(true);
        pause();
        saveAudio(true);
        return;
      }
      mediaRecorder.resume();
      setPauseAudio(false);
      start();
    }
  };

  const renderSendButtonContent = () => {
    if (recordingAudio) {
      return (
        <button
          onClick={handleSendButtonClick}
          className="send_voice_btn btn btn-primary btn-small flex flex-ai-c flex-columngap-8"
        >
          <span>{t("SEND")}</span>

          <img
            src={NewSendIcon}
            className="send_voice_Icon"
            alt="send_voice_Icon"
          />
        </button>
      );
    }

    return (
      <div
        className="start_record_btn flex flex-ai-c"
        onClick={handleClickStartRecordBtn}
      >
        <MicIconComponent />
      </div>
    );
  };

  const renderTimer = recordingAudio && (
    <div className="voice_recorder__timer">{`${minutes}:${seconds}`}</div>
  );

  const renderDiscardButton = recordingAudio && (
    <div
      className="voice_recorder__icon_wrapper"
      onClick={handleDiscardBtnClick}
    >
      <img
        className="voice_recorder__icon"
        alt="discard"
        src={TrashFilledIcon}
      />
    </div>
  );

  const renderPauseResumeBtn = () => {
    const icon = pauseAudio ? (
      <MicIconComponent className="voice_recorder__icon voice_recorder__pause_resume_icon" />
    ) : (
      <PauseIconComponent className="voice_recorder__icon voice_recorder__pause_resume_icon" />
    );
    // const alt = pauseAudio ? "resume" : "pause";

    if (recordingAudio) {
      return (
        <div
          className="voice_recorder__icon_wrapper "
          onClick={handlePauseVoiceRecording}
        >
          {icon}
          {/* <img className="" src={icon} alt={alt} /> */}
        </div>
      );
    }

    return null;
  };

  const renderAudioControl = () => {
    if (!recordingAudio) return null;

    if (pauseAudio && file) {
      return (
        <div className="audio_control_wrapper">
          <audio controls>
            <source src={file.fileData} type="audio/ogg" />
            <source src={file.fileData} type="audio/mpeg" />
          </audio>
        </div>
      );
    }
    return (
      <div className="flex">
        <AudioAnalyser audio={recordingAudio} />
      </div>
    );
  };

  useEffect(() => {
    if (mediaRecorder) {
      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          setChunks((prev) => {
            return [...prev, e.data];
          });
        }
      };
    }
  }, [mediaRecorder]);

  // key binding for sending on enter click
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e && e.key === "Enter" && voiceRecording) {
        handleSendButtonClick();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  useEffect(() => {
    handleDiscardBtnClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.conversation?.id]);

  return (
    <div
      className={`flex flex-ai-c voice_recorder ${
        voiceRecording ? "voice_recorder--recording" : ""
      }`}
    >
      <div className="flex ">
        {renderPauseResumeBtn()}
        {renderAudioControl()}
        {renderTimer}
        {renderDiscardButton}
      </div>
      <div className="flex flex-jc-c voice_recorder__start_send_wrapper">
        {renderSendButtonContent()}
      </div>
    </div>
  );
}

export default VoiceRecorder;
